import { useDropzone } from 'react-dropzone';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Mail, Plus, Eye, Trash2, ClipboardList, UserMinus, ChevronDown, Copy, Check, KeyRound, UserPlus, Send, AlertTriangle, Loader2, Boxes, FileUp, FileText, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getOrg, watchOrgInvitations, watchAuditsForOrg, watchOrgMembers, watchOrgDocumentsAdmin, type OrgMember } from '@/lib/firestore';
import {
  callInviteUser, callAdminAssumeOrg,
  callSetMemberRole, callRemoveMember, callRevokeInvitation,
  callCreateUserDirect, type CreateUserDirectResp,
  callSetOrgToolkits, callCreateOrgDocument, callFinalizeOrgDocument, callSetDocumentPublished, callDeleteOrgDocument,
  callSignedDownload,
} from '@/lib/functions';
import { uploadOrgDocument } from '@/lib/storage';
import { fmtRelative, fmtBytes, statusColor, STATUS_KEY } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/ui/loader';
import { Button, buttonClassName } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input, inputClassName } from '@/components/ui/input';
import { Label, labelClassName } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { TOOLKITS } from '@/lib/toolkits';
import type { Organization, Invitation, AuditDoc, DocumentDoc, ToolkitId, DocumentKind } from '@/types/models';

export default function AdminOrgDetail() {
  const { orgId } = useParams();
  const { refresh } = useAuth();
  const { t, locale } = useT();
  const nav = useNavigate();
  const [org, setOrg] = useState<Organization | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [audits, setAudits] = useState<AuditDoc[]>([]);
  const [orgDocs, setOrgDocs] = useState<DocumentDoc[]>([]);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    getOrg(orgId).then(setOrg);
    const a = watchOrgInvitations(orgId, setInvitations);
    const b = watchAuditsForOrg(orgId, setAudits);
    const c = watchOrgMembers(orgId, setMembers);
    const d = watchOrgDocumentsAdmin(orgId, setOrgDocs);
    return () => { a(); b(); c(); d(); };
  }, [orgId]);

  const assume = async (redirectTo = '/') => {
    if (!orgId) return;
    await callAdminAssumeOrg({ orgId });
    await refresh();
    toast.success(t('admin.org.assumed', { name: org?.displayName ?? '' }));
    nav(redirectTo);
  };

  if (!org) return <Loader fullscreen />;

  return (
    <div className="space-y-6">
      <Link to="/admin/orgs" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-brand-600">
        <ArrowLeft className="h-4 w-4" /> {t('admin.org.back')}
      </Link>

      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-display text-2xl text-ink-900 dark:text-white">{org.displayName}</h1>
          <div className="mt-1 text-xs text-ink-500">
            <code>{org.id}</code>
            {' · '}
            {t('admin.org.contact', { email: org.primaryContact?.email ?? '—' })}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => assume()}><Eye className="h-4 w-4" /> {t('admin.org.assume')}</Button>
          <Button onClick={() => setShow(true)}><Plus className="h-4 w-4" /> {t('admin.org.invite')}</Button>
        </div>
      </header>

      <section>
        <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-white">{t('admin.org.audits', { n: audits.length })}</h2>
        {audits.length === 0
          ? <Card className="text-sm text-ink-500">{t('admin.org.no_audits')}</Card>
          : <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('portal.col.audit')}</TableHeadCell>
                  <TableHeadCell>{t('common.status')}</TableHeadCell>
                  <TableHeadCell>{t('common.created')}</TableHeadCell>
                </tr>
              </TableHead>
              <TableBody>
                {audits.map(a => (
                  <TableRow key={a.id} className="hover:bg-slate-50 dark:hover:bg-muted/50">
                    <TableCell><Link to={`/audits/${a.id}`} className="font-medium hover:text-brand-600">{a.displayName}</Link></TableCell>
                    <TableCell><span className={statusColor(a.status)}>{t(STATUS_KEY[a.status])}</span></TableCell>
                    <TableCell className="text-ink-500">{fmtRelative(a.createdAt, t, locale)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        }
      </section>

      <Card>
        <div className="flex items-center gap-3">
          <ClipboardList className="h-5 w-5 text-brand-500" />
          <div className="flex-1">
            <div className="font-medium text-ink-900 dark:text-white">{t('admin.org.fill_q')}</div>
            <div className="text-xs text-ink-500">{t('admin.org.fill_q.desc')}</div>
          </div>
          <Button onClick={() => assume('/questionnaire')}>{t('admin.org.fill_q.start')}</Button>
        </div>
      </Card>

      {/* ── Entitlements ─────────────────────────────────────────────── */}
      {orgId && org && (
        <ToolkitsSection orgId={orgId} currentToolkits={org.toolkits ?? ['audit']} onUpdated={updated => setOrg(o => o ? { ...o, toolkits: updated } : o)} />
      )}

      {/* ── Documents ────────────────────────────────────────────────── */}
      {orgId && (
        <AdminDocumentsSection orgId={orgId} docs={orgDocs} />
      )}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg text-ink-900 dark:text-white">{t('admin.org.members', { n: members.length })}</h2>
        </div>
        {members.length === 0
          ? <Card className="text-sm text-ink-500">{t('admin.org.no_members')}</Card>
          : <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('admin.org.member.col.user')}</TableHeadCell>
                  <TableHeadCell>{t('admin.org.member.col.email')}</TableHeadCell>
                  <TableHeadCell>{t('admin.org.member.col.role')}</TableHeadCell>
                  <TableHeadCell>{t('admin.org.member.col.joined')}</TableHeadCell>
                  <TableHeadCell className="text-right">{t('common.actions')}</TableHeadCell>
                </tr>
              </TableHead>
              <TableBody>
                {members.map(m => (
                  <MemberRow key={m.uid} member={m} orgId={orgId!} />
                ))}
              </TableBody>
            </Table>
        }
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg text-ink-900 dark:text-white">{t('admin.org.invitations')}</h2>
        </div>
        {invitations.filter(i => i.status === 'pending').length === 0 && invitations.length === 0
          ? <Card className="text-sm text-ink-500">{t('admin.org.no_invitations')}</Card>
          : <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('admin.org.inv.col.email')}</TableHeadCell>
                  <TableHeadCell>{t('admin.org.inv.col.role')}</TableHeadCell>
                  <TableHeadCell>{t('common.status')}</TableHeadCell>
                  <TableHeadCell>{t('admin.org.inv.col.sent')}</TableHeadCell>
                  <TableHeadCell />
                </tr>
              </TableHead>
              <TableBody>
                {invitations.map(i => (
                  <TableRow key={i.id}>
                    <TableCell><Mail className="mr-1.5 inline h-3.5 w-3.5 text-ink-400" />{i.email}</TableCell>
                    <TableCell className="text-ink-500">{i.role === 'org_admin' ? t('admin.org.member.role.admin') : t('admin.org.member.role.member')}</TableCell>
                    <TableCell><span className={statusColor(i.status)}>{t(STATUS_KEY[i.status])}</span></TableCell>
                    <TableCell className="text-ink-500">{fmtRelative(i.createdAt, t, locale)}</TableCell>
                    <TableCell className="text-right">
                      {i.status === 'pending' && (
                        <button
                          onClick={async () => {
                            if (!confirm(t('admin.org.inv.revoke_confirm', { email: i.email }))) return;
                            try { await callRevokeInvitation({ inviteId: i.id }); toast.success(t('admin.org.inv.revoked')); }
                            catch (e) { toast.error((e as Error).message); }
                          }}
                          className={buttonClassName('ghost', 'text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30')}
                          title={t('admin.org.inv.title')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        }
      </section>

      {/* Floating "invite" trigger reachable from Members section too */}
      {show && orgId && (
        <AddUserModal orgId={orgId} orgName={org.displayName} onClose={() => setShow(false)} />
      )}
    </div>
  );
}

// ─── Toolkits / entitlements section ─────────────────────────────────────────
function ToolkitsSection({ orgId, currentToolkits, onUpdated }: {
  orgId: string;
  currentToolkits: ToolkitId[];
  onUpdated: (ids: ToolkitId[]) => void;
}) {
  const { t } = useT();
  const [saving, setSaving] = useState(false);

  const toggle = async (id: ToolkitId, checked: boolean) => {
    const next: ToolkitId[] = checked
      ? [...new Set([...currentToolkits, id])]
      : currentToolkits.filter(tk => tk !== id);
    setSaving(true);
    try {
      await callSetOrgToolkits({ orgId, toolkits: next });
      onUpdated(next);
      toast.success(t('admin.org.toolkits.updated'));
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-ink-900 dark:text-white">
        <Boxes className="h-5 w-5 text-brand-500" />
        {t('admin.org.toolkits.title')}
      </h2>
      <Card>
        <div className="divide-y divide-slate-100 dark:divide-border/30">
          {TOOLKITS.map(tk => {
            const active = currentToolkits.includes(tk.id);
            return (
              <div key={tk.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                <div className="flex items-center gap-3">
                  <tk.icon className="h-4 w-4 text-ink-500" />
                  <div>
                    <div className="font-medium text-ink-900 dark:text-white text-sm">{t(tk.labelKey)}</div>
                    <div className="text-xs text-ink-500 dark:text-muted-foreground">{tk.toolkitPath}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(tk.id, !active)}
                  disabled={saving}
                  className="text-brand-500 disabled:opacity-40"
                  title={active ? t('admin.org.toolkits.disable') : t('admin.org.toolkits.enable')}
                >
                  {active
                    ? <ToggleRight className="h-7 w-7" />
                    : <ToggleLeft className="h-7 w-7 text-ink-400" />
                  }
                </button>
              </div>
            );
          })}
        </div>
      </Card>
    </section>
  );
}

// ─── Admin documents section ─────────────────────────────────────────────────
function AdminDocumentsSection({ orgId, docs }: { orgId: string; docs: DocumentDoc[] }) {
  const { t } = useT();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [kind, setKind] = useState<DocumentKind>('scope');
  const [description, setDescription] = useState('');
  const { user } = useAuth();

  const kindOptions: { value: DocumentKind; labelKey: keyof typeof import('@/lib/i18n.messages').messages }[] = [
    { value: 'scope', labelKey: 'documents.kind.scope' },
    { value: 'report', labelKey: 'documents.kind.report' },
    { value: 'presentation', labelKey: 'documents.kind.presentation' },
    { value: 'contract', labelKey: 'documents.kind.contract' },
    { value: 'invoice', labelKey: 'documents.kind.invoice' },
    { value: 'other', labelKey: 'documents.kind.other' },
  ];

  const startUpload = async (file: File) => {
    if (!description.trim()) return toast.error(t('admin.org.documents.description_required'));
    if (!user) return;
    setUploading(true);
    setProgress(0);
    try {
      const title = file.name.replace(/\.[^/.]+$/, '');
      const { data } = await callCreateOrgDocument({ orgId, kind, title, description: description.trim() });
      const { promise, fileName } = uploadOrgDocument(orgId, data.docId, file, p => setProgress(p.percent), user.uid);
      await promise;
      await callFinalizeOrgDocument({ docId: data.docId, fileName });
      toast.success(t('admin.org.documents.uploaded'));
      setDescription('');
    } catch (e) { toast.error((e as Error).message); }
    finally { setUploading(false); }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1, multiple: false, disabled: uploading,
    onDrop: files => files[0] && startUpload(files[0]),
  });

  const togglePublish = async (doc: DocumentDoc) => {
    try {
      await callSetDocumentPublished({ docId: doc.id, published: !doc.publishedToClient });
      toast.success(doc.publishedToClient ? t('admin.org.documents.unpublished') : t('admin.org.documents.published'));
    } catch (e) { toast.error((e as Error).message); }
  };

  const deleteDoc = async (doc: DocumentDoc) => {
    if (!confirm(t('admin.org.documents.delete_confirm'))) return;
    try {
      await callDeleteOrgDocument({ docId: doc.id });
      toast.success(t('admin.org.documents.deleted'));
    } catch (e) { toast.error((e as Error).message); }
  };

  const download = async (doc: DocumentDoc) => {
    if (!doc.filePath) return;
    try {
      const { data } = await callSignedDownload({ kind: 'document', path: doc.filePath });
      window.location.href = data.url;
    } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-ink-900 dark:text-white">
        <FileText className="h-5 w-5 text-brand-500" />
        {t('admin.org.documents.title')}
      </h2>

      <Card className="mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>{t('admin.org.documents.description_label')}</Label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className={inputClassName(undefined, 'mt-1 min-h-[80px] resize-y')}
              placeholder={t('admin.org.documents.description_placeholder')}
              disabled={uploading}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>{t('admin.org.documents.type_label')}</Label>
            <select
              value={kind}
              onChange={e => setKind(e.target.value as DocumentKind)}
              disabled={uploading}
              className={inputClassName(undefined, 'mt-1')}
            >
              {kindOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
          </div>
        </div>

        <div
          {...getRootProps()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition ${
            isDragActive ? 'border-brand-400 bg-brand-50/40 dark:bg-brand-900/10' : 'border-slate-300 bg-slate-50 hover:border-brand-300 dark:border-border dark:bg-muted/40'
          } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        >
          <input {...getInputProps()} />
          <FileUp className="h-7 w-7 text-brand-500" />
          <div className="mt-2 text-sm font-medium text-ink-900 dark:text-white">
            {isDragActive ? t('admin.org.documents.drop_active') : t('admin.org.documents.drop_idle')}
          </div>
          <div className="mt-1 text-xs text-ink-500">{t('admin.org.documents.drop_hint')}</div>
        </div>

        {uploading && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-ink-500">
              <span>{t('admin.org.documents.uploading')}</span><span className="font-mono">{progress.toFixed(0)}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-secondary">
              <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </Card>

      {docs.length > 0 && (
        <Card>
          <ul className="divide-y divide-slate-200 dark:divide-border/50">
            {docs.map(doc => (
              <li key={doc.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <FileText className="h-4 w-4 shrink-0 text-ink-400" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-ink-900 dark:text-white text-sm">{doc.description || doc.title}</div>
                  <div className="text-xs text-ink-500">
                    {t(KIND_LABEL_KEY[doc.kind])} · {doc.sizeBytes ? fmtBytes(doc.sizeBytes) : '—'}
                    {doc.publishedToClient
                      ? <Badge variant="success" className="ml-2">{t('admin.org.documents.badge.published')}</Badge>
                      : <Badge variant="warning" className="ml-2">{t('admin.org.documents.badge.draft')}</Badge>
                    }
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {doc.filePath && (
                    <button onClick={() => download(doc)} title={t('admin.org.documents.download')} className="grid h-7 w-7 place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-secondary text-ink-500">
                      <FileText className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => togglePublish(doc)}
                    title={doc.publishedToClient ? t('admin.org.documents.unpublish') : t('admin.org.documents.publish')}
                    className="grid h-7 w-7 place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-secondary text-brand-500"
                  >
                    {doc.publishedToClient ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4 text-ink-400" />}
                  </button>
                  <button
                    onClick={() => deleteDoc(doc)}
                    title={t('admin.org.documents.delete')}
                    className="grid h-7 w-7 place-items-center rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

const KIND_LABEL_KEY = {
  report:        'documents.kind.report',
  survey:        'documents.kind.survey',
  scope:         'documents.kind.scope',
  presentation:  'documents.kind.presentation',
  contract:      'documents.kind.contract',
  invoice:       'documents.kind.invoice',
  other:         'documents.kind.other',
} as const satisfies Record<DocumentKind, keyof typeof import('@/lib/i18n.messages').messages>;

// ─── Add user modal — two paths: direct creation or invitation ─────────────
function AddUserModal({ orgId, orgName, onClose }: { orgId: string; orgName: string; onClose: () => void }) {
  const [mode, setMode] = useState<'direct' | 'invite'>('direct');
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState<CreateUserDirectResp | null>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      if (mode === 'direct') {
        const { data } = await callCreateUserDirect({
          orgId,
          email:       String(f.get('email')),
          displayName: String(f.get('displayName') ?? ''),
          role:        String(f.get('role')) as 'org_admin' | 'org_member',
          sendResetEmail: true,
        });
        setCredentials(data);
      } else {
        const { data } = await callInviteUser({
          orgId,
          email: String(f.get('email')),
          role:  String(f.get('role')) as 'org_admin' | 'org_member',
        });
        toast.success(
          data.passwordEmailSent
            ? 'Compte ajouté à l’organisation. Un email pour choisir le mot de passe a été envoyé (valable 1 heure).'
            : 'Compte ajouté. L’email d’invitation contient le lien pour choisir le mot de passe (valable 1 heure).',
        );
        onClose();
      }
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  // ─── Post-creation: show credentials once ───────────────────────
  if (credentials) {
    return (
      <Dialog
        open
        onClose={onClose}
        title="Compte créé"
        footer={<Button onClick={onClose}>J'ai noté les identifiants</Button>}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Check className="h-4 w-4" />
          </div>
          <p className="text-sm text-ink-500">
            L'utilisateur peut désormais se connecter avec ces identifiants.
            <strong className="text-rose-600 dark:text-rose-400"> Ces informations ne seront plus affichées</strong> —
            transmettez-les à votre client via un canal sécurisé.
          </p>
        </div>
        <div className="space-y-3">
          <CopyField label="Adresse courriel" value={credentials.email} />
          <CopyField label="Mot de passe temporaire" value={credentials.tempPassword} secret />
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            Demandez à l'utilisateur de modifier ce mot de passe à sa première connexion (Paramètres → Réinitialiser).
          </div>
          {credentials.passwordResetLink && (
            <details className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-border dark:bg-muted/40">
              <summary className="cursor-pointer font-medium text-ink-700 dark:text-foreground/80">
                Alternative : lien de réinitialisation de mot de passe
              </summary>
              <p className="mt-2 text-ink-500">
                Au lieu de partager le mot de passe temporaire, vous pouvez envoyer ce lien (valable 1h) :
              </p>
              <CopyField label="" value={credentials.passwordResetLink} className="mt-2" />
            </details>
          )}
        </div>
      </Dialog>
    );
  }

  // ─── Form ───────────────────────────────────────────────────────
  return (
    <Dialog
      open
      onClose={onClose}
      title="Ajouter un utilisateur"
      description={`Pour ${orgName}`}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button type="submit" form="add-user-form" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
             mode === 'direct' ? <UserPlus className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {mode === 'direct' ? 'Créer le compte' : "Envoyer l'invitation"}
          </Button>
        </>
      }
    >
      <form id="add-user-form" onSubmit={submit} className="space-y-4">
        {/* Tabs */}
        <div className="inline-flex w-full rounded-lg bg-surface p-1 text-xs dark:bg-muted">
          <button type="button" onClick={() => setMode('direct')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium transition ${
              mode === 'direct'
                ? 'bg-white text-ink-900 shadow-sm dark:bg-secondary dark:text-white'
                : 'text-ink-500 dark:text-muted-foreground'
            }`}>
            <UserPlus className="h-3.5 w-3.5" />
            <span>Créer le compte</span>
            <Badge variant="success" className="ml-1 text-[0.6rem]">Recommandé</Badge>
          </button>
          <button type="button" onClick={() => setMode('invite')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium transition ${
              mode === 'invite'
                ? 'bg-white text-ink-900 shadow-sm dark:bg-secondary dark:text-white'
                : 'text-ink-500 dark:text-muted-foreground'
            }`}>
            <Send className="h-3.5 w-3.5" />
            <span>Inviter par email</span>
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-ink-600 dark:border-border dark:bg-muted/40 dark:text-muted-foreground">
          {mode === 'direct'
            ? <><KeyRound className="mr-1 inline h-3.5 w-3.5 text-brand-500" /> Le compte est créé immédiatement avec un mot de passe sécurisé. Aucune action requise du client — vous lui transmettez les identifiants vous-même.</>
            : <><Send className="mr-1 inline h-3.5 w-3.5 text-brand-500" /> Le compte est créé et rattaché à l’organisation. La personne reçoit un email pour choisir son mot de passe (lien valable 1 heure), puis se connecte. Si le lien expire, « Mot de passe oublié » sur la page de connexion en envoie un nouveau.</>
          }
        </div>

        <div className="grid gap-4">
          {mode === 'direct' && (
            <div>
              <Label>Nom complet</Label>
              <Input name="displayName" required className="mt-1" placeholder="Marie Tremblay" />
            </div>
          )}
          <div>
            <Label>Adresse courriel</Label>
            <Input type="email" name="email" required className="mt-1" placeholder="marie@acme.com" />
          </div>
          <div>
            <Label>Rôle</Label>
            <select name="role" required className={inputClassName(undefined, 'mt-1')} defaultValue="org_member">
              <option value="org_member">Membre — accès lecture/écriture standard</option>
              <option value="org_admin">Administrateur de l'organisation — peut gérer les autres utilisateurs</option>
            </select>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function CopyField({ label, value, secret, className }: { label: string; value: string; secret?: boolean; className?: string }) {
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(!secret);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={className}>
      {label && <div className={`${labelClassName()} mb-1`}>{label}</div>}
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-border dark:bg-muted/50">
        <code className="flex-1 truncate font-mono text-xs text-ink-900 dark:text-white">
          {reveal ? value : '•'.repeat(Math.min(20, value.length))}
        </code>
        {secret && (
          <button type="button" onClick={() => setReveal(r => !r)} className="text-xs text-ink-500 hover:text-brand-600">
            {reveal ? 'masquer' : 'afficher'}
          </button>
        )}
        <button type="button" onClick={copy} className="grid h-7 w-7 place-items-center rounded-md hover:bg-slate-200 dark:hover:bg-secondary" title="Copier">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-ink-500" />}
        </button>
      </div>
    </div>
  );
}

// ─── Member row with inline role + remove actions ──────────────────────────
function MemberRow({ member, orgId }: { member: OrgMember; orgId: string }) {
  const { t, locale } = useT();
  const [busy, setBusy] = useState(false);

  const changeRole = async (next: 'org_admin' | 'org_member') => {
    if (next === member.role) return;
    setBusy(true);
    try {
      await callSetMemberRole({ orgId, uid: member.uid, role: next });
      toast.success(t('admin.org.member.role_updated', {
        role: next === 'org_admin' ? t('admin.org.member.role.admin') : t('admin.org.member.role.member'),
      }));
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(t('admin.org.member.remove_confirm', { who: member.email ?? member.uid }))) return;
    setBusy(true);
    try {
      await callRemoveMember({ orgId, uid: member.uid });
      toast.success(t('admin.org.member.removed'));
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <TableRow className={busy ? 'opacity-50' : ''}>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-100 text-[0.7rem] font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
            {(member.displayName ?? member.email ?? '?').charAt(0).toUpperCase()}
          </div>
          <span className="font-medium text-ink-900 dark:text-white">{member.displayName ?? '—'}</span>
        </div>
      </TableCell>
      <TableCell className="text-ink-500"><Mail className="mr-1.5 inline h-3.5 w-3.5 text-ink-400" />{member.email ?? '—'}</TableCell>
      <TableCell>
        <div className="relative inline-block">
          <select
            value={member.role}
            onChange={e => changeRole(e.target.value as 'org_admin' | 'org_member')}
            disabled={busy}
            className="appearance-none rounded-md border border-slate-200 bg-white px-2.5 py-1 pr-7 text-xs font-medium text-ink-900 hover:border-brand-300 dark:border-border dark:bg-card dark:text-white"
          >
            <option value="org_member">{t('admin.org.member.role.member')}</option>
            <option value="org_admin">{t('admin.org.member.role.admin')}</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-400" />
        </div>
      </TableCell>
      <TableCell className="text-ink-500">{member.joinedAt ? fmtRelative(member.joinedAt, t, locale) : '—'}</TableCell>
      <TableCell className="text-right">
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          className={buttonClassName('ghost', 'text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30')}
          title={t('admin.org.member.remove_title')}
        >
          <UserMinus className="h-3.5 w-3.5" /> {t('admin.org.member.remove')}
        </button>
      </TableCell>
    </TableRow>
  );
}
